import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map, Observable } from "rxjs";
import {
  ReferenceLanguage,
  RawApiTranslationData,
  TranslationApiResponse,
  TranslationCatalog,
  UserLanguagePreference,
} from "@core/models/translation/translation.models";
import {
  TRANSLATION_API_PATHS,
  USE_LOCAL_TRANSLATION_FALLBACK,
  LOCAL_I18N_ASSET_VERSION,
} from "@core/constants/translation.config";
import { DEFAULT_LANGUAGE_CODE } from "@core/constants/languages";
import { BaseApiService } from "@core/services/base/base-api.service";
import { flattenTranslationKeys } from "./translation-flat.util";
import { Translation } from "@jsverse/transloco";

/**
 * Converts the raw API `data` payload into a normalised `TranslationCatalog`.
 *
 * The live API returns translations in two forms inside `data`:
 *  1. `data.translations` – flat dot-notation keys (`ui.*`), used directly.
 *  2. Root-level nested objects (e.g. `data.legal`, `data.privacy`,
 *     `data.termsAndConditions`) – each object's string values are merged
 *     into the flat map, dropping the parent key so that template keys like
 *     `'termsAndConditionsTitle' | transloco` resolve correctly.
 *  3. Root-level string properties – added as-is.
 */
export function normalizeApiTranslationData(
  raw: RawApiTranslationData,
): TranslationCatalog {
  const reserved = new Set(["defaultLocale", "translations", "version"]);
  const merged: Record<string, string> = { ...raw.translations };

  for (const [key, value] of Object.entries(raw)) {
    if (reserved.has(key)) continue;

    if (typeof value === "string") {
      // Root-level string property — use as-is
      merged[key] = value;
    } else if (value !== null && typeof value === "object") {
      // Nested translation group (legal, privacy, termsAndConditions, …)
      // Flatten string values directly into merged, dropping the parent key.
      for (const [innerKey, innerValue] of Object.entries(
        value as Record<string, unknown>,
      )) {
        if (typeof innerValue === "string") {
          merged[innerKey] = innerValue;
        }
      }
    }
  }

  return {
    version: (raw as unknown as TranslationCatalog).version ?? "live",
    defaultLocale: raw.defaultLocale,
    translations: merged,
  };
}

@Injectable({ providedIn: "root" })
export class TranslationApiService extends BaseApiService {
  constructor(http: HttpClient) {
    super(http);
  }

  getReferenceLanguages(): Observable<TranslationApiResponse<ReferenceLanguage[]>> {
    return this.get<TranslationApiResponse<ReferenceLanguage[]>>(
      TRANSLATION_API_PATHS.languages,
    );
  }

  updateUserLanguage(
    locale: string,
  ): Observable<TranslationApiResponse<UserLanguagePreference>> {
    return this.put<TranslationApiResponse<UserLanguagePreference>>(
      TRANSLATION_API_PATHS.userLanguage,
      null,
      { headers: { "Accept-Language": locale } },
    );
  }

  getTranslationCatalog(
    locale: string,
  ): Observable<TranslationApiResponse<TranslationCatalog>> {
    if (!USE_LOCAL_TRANSLATION_FALLBACK) {
      // Live API: locale is sent via Accept-Language header, not in the URL path.
      // Response keys are normalised so all Transloco paths resolve correctly.
      return this.get<TranslationApiResponse<RawApiTranslationData>>(
        TRANSLATION_API_PATHS.translations,
        { headers: { "Accept-Language": locale } },
      ).pipe(
        map((response) => ({
          status: response.status,
          data: normalizeApiTranslationData(response.data),
        })),
      );
    }

    // Cache-bust so updated locale JSON (new ui.common.* keys) is always fetched.
    return this.http
      .get<Translation>(
        `/assets/i18n/${locale}.json?v=${LOCAL_I18N_ASSET_VERSION}`,
      )
      .pipe(
        map((nested) => ({
          status: "success" as const,
          data: {
            version: LOCAL_I18N_ASSET_VERSION,
            defaultLocale: DEFAULT_LANGUAGE_CODE,
            translations: flattenTranslationKeys(nested),
          },
        })),
      );
  }
}
